'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  name: string;
  included: boolean;
  tooltip?: string;
}

interface PricingCardProps {
  title: string;
  price: number;
  period: string;
  description: string;
  features: Feature[];
  isPopular?: boolean;
  ctaText: string;
  onCtaClick: () => void;
  subscriberCount?: string;
}

export default function PricingCard({
  title,
  price,
  period,
  description,
  features,
  isPopular = false,
  ctaText,
  onCtaClick,
  subscriberCount
}: PricingCardProps) {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  return (
    <div className={`relative bg-card border ${isPopular ? 'border-primary shadow-cyan-glow' : 'border-border'} rounded-2xl p-8 transition-all duration-200 hover:scale-105`}>
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-6 py-1 rounded-full text-sm font-heading font-semibold">
          Most Popular
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-2xl font-heading font-bold text-foreground mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center">
          <span className="text-5xl font-heading font-bold text-foreground">${price}</span>
          <span className="text-muted-foreground ml-2">/{period}</span>
        </div>
        {subscriberCount && (
          <p className="text-xs text-muted-foreground mt-2">{subscriberCount} active subscribers</p>
        )}
      </div>

      <button
        onClick={onCtaClick}
        className={`w-full py-3 rounded-lg font-heading font-semibold transition-all duration-200 mb-6 ${
          isPopular
            ? 'bg-primary text-primary-foreground hover:scale-105 hover:shadow-cyan-glow'
            : 'bg-muted text-foreground hover:bg-primary hover:text-primary-foreground'
        }`}
      >
        {ctaText}
      </button>

      <div className="space-y-3">
        {features.map((feature, index) => (
          <div
            key={index}
            className="flex items-start space-x-3 relative"
            onMouseEnter={() => feature.tooltip && setHoveredFeature(index)}
            onMouseLeave={() => setHoveredFeature(null)}
          >
            <Icon
              name={feature.included ? 'CheckCircleIcon' : 'XCircleIcon'}
              variant="solid"
              size={20}
              className={feature.included ? 'text-success' : 'text-muted-foreground'}
            />
            <span className={`text-sm ${feature.included ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
              {feature.name}
            </span>
            {feature.tooltip && (
              <Icon
                name="InformationCircleIcon"
                size={16}
                className="text-muted-foreground cursor-help"
              />
            )}
            {feature.tooltip && hoveredFeature === index && (
              <div className="absolute left-0 top-8 bg-popover border border-border rounded-lg p-3 shadow-lg z-10 w-64">
                <p className="text-xs text-popover-foreground">{feature.tooltip}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}